// client/src/coms/views/canvas_parts/CanvasTopMenuBar.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ImportedBrushImportModal from './ImportedBrushImportModal';
import CanvasPaperSurfaceSubmenu, { CANVAS_MENU_FLYOUT_ATTRIBUTE } from './CanvasPaperSurfaceSubmenu';
import { normaliseLiquifySettings } from './canvas_room_view_parts/liquifyUtils';
import { clampDocumentDimension } from './canvasRoomUtils';

function clampNumber(value, min, max, fallback = min) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.max(min, Math.min(max, number));
}

const SIZE_SLIDER_UI_MAX = 1000;
const SIZE_SLIDER_LINEAR_MAX = 20;
const SIZE_SLIDER_LINEAR_UI_PORTION = 0.45;
const SIZE_SLIDER_ACCELERATION = 1.8;
const SIZE_SLIDER_POST_LINEAR_BLEND = 0.2;

function getSizeSliderLinearUiMax() {
  return Math.round(SIZE_SLIDER_UI_MAX * SIZE_SLIDER_LINEAR_UI_PORTION);
}

function realToSizeSliderValue(value, min, max) {
  const safeMin = Number.isFinite(Number(min)) ? Number(min) : 0;
  const safeMax = Number.isFinite(Number(max)) ? Number(max) : 100;
  if (safeMax <= safeMin) return 0;

  const safeValue = clampNumber(value, safeMin, safeMax, safeMin);
  const linearMax = clampNumber(SIZE_SLIDER_LINEAR_MAX, safeMin, safeMax, safeMax);
  const linearRange = Math.max(1, linearMax - safeMin);
  const linearUiMax = getSizeSliderLinearUiMax();

  if (safeValue <= linearMax || safeMax <= linearMax) {
    return Math.round(((safeValue - safeMin) / linearRange) * linearUiMax);
  }

  const afterLinearRange = safeMax - linearMax;
  const afterLinearUiRange = SIZE_SLIDER_UI_MAX - linearUiMax;
  const normalisedValue = clampNumber((safeValue - linearMax) / afterLinearRange, 0, 1, 0);
  let low = 0;
  let high = 1;

  for (let index = 0; index < 22; index += 1) {
    const mid = (low + high) / 2;
    const eased = (SIZE_SLIDER_POST_LINEAR_BLEND * mid) + ((1 - SIZE_SLIDER_POST_LINEAR_BLEND) * Math.pow(mid, SIZE_SLIDER_ACCELERATION));
    if (eased < normalisedValue) low = mid;
    else high = mid;
  }

  return Math.round(linearUiMax + (high * afterLinearUiRange));
}

function sizeSliderValueToReal(value, min, max) {
  const safeMin = Number.isFinite(Number(min)) ? Number(min) : 0;
  const safeMax = Number.isFinite(Number(max)) ? Number(max) : 100;
  if (safeMax <= safeMin) return safeMin;

  const sliderValue = clampNumber(value, 0, SIZE_SLIDER_UI_MAX, 0);
  const linearMax = clampNumber(SIZE_SLIDER_LINEAR_MAX, safeMin, safeMax, safeMax);
  const linearRange = Math.max(1, linearMax - safeMin);
  const linearUiMax = getSizeSliderLinearUiMax();

  if (sliderValue <= linearUiMax || safeMax <= linearMax) {
    return Math.round(safeMin + ((sliderValue / linearUiMax) * linearRange));
  }

  const afterLinearUiRange = SIZE_SLIDER_UI_MAX - linearUiMax;
  const afterLinearRange = safeMax - linearMax;
  const normalised = clampNumber((sliderValue - linearUiMax) / afterLinearUiRange, 0, 1, 0);
  const eased = (SIZE_SLIDER_POST_LINEAR_BLEND * normalised) + ((1 - SIZE_SLIDER_POST_LINEAR_BLEND) * Math.pow(normalised, SIZE_SLIDER_ACCELERATION));
  return Math.round(linearMax + (eased * afterLinearRange));
}

function getViewportToolSizeLimit() {
  if (typeof window === 'undefined') return 320;
  const width = Number(window.innerWidth) || 320;
  const height = Number(window.innerHeight) || 320;
  const safeSmallSide = Math.max(32, Math.min(width, height) - 96);
  return Math.round(clampNumber(safeSmallSide, 32, 1600, 320));
}

function useViewportToolSizeLimit() {
  const [limit, setLimit] = useState(getViewportToolSizeLimit);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setLimit(getViewportToolSizeLimit());
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return limit;
}


function getIsMobileTopBar() {
  if (typeof window === 'undefined') return false;
  return (Number(window.innerWidth) || 0) <= 760;
}

function useIsMobileTopBar() {
  const [isMobile, setIsMobile] = useState(getIsMobileTopBar);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setIsMobile(getIsMobileTopBar());
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return isMobile;
}

function TopBarButton({ children, onClick, disabled, title, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        height: 28,
        minWidth: 30,
        borderRadius: 5,
        border: danger ? '1px solid rgba(248,113,113,0.30)' : '1px solid rgba(255,255,255,0.10)',
        background: disabled ? 'rgba(255,255,255,0.025)' : danger ? 'rgba(127,29,29,0.24)' : 'rgba(255,255,255,0.055)',
        color: disabled ? 'rgba(255,255,255,0.30)' : danger ? 'rgba(255,235,235,0.98)' : 'rgba(235,238,242,0.94)',
        padding: '0 9px',
        fontSize: 12,
        fontWeight: 850,
        cursor: disabled ? 'default' : 'pointer',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}


function FileDropdownButton({
  onSaveCanvas,
  onSaveCanvasProject,
  onLoadCanvasProject,
  onImportCanvasImage,
  onExportModel,
  modelViewOpen,
  onToggleModelView,
  onOpenCanvasAreas,
  canOpenCanvasAreas,
  onOpenCanvasGame,
  canOpenCanvasGame,
  onOpenPartyGames,
  onEnableAnimation,
  onDisableAnimation,
  canEnableAnimation,
  animationEnabled,
  showDocumentSize,
  draftDocumentWidth,
  draftDocumentHeight,
  setDraftDocumentWidth,
  setDraftDocumentHeight,
  submitDocumentSize,
  savingDocumentSize,
  gameMode = null,
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;

    const closeFromOutside = (event) => {
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      // Submenu flyouts are portalled to the body but belong to this menu.
      if (event.target && typeof event.target.closest === 'function' && event.target.closest(`[${CANVAS_MENU_FLYOUT_ATTRIBUTE}]`)) return;
      setOpen(false);
    };

    window.addEventListener('pointerdown', closeFromOutside, true);
    window.addEventListener('keydown', closeFromOutside, true);
    return () => {
      window.removeEventListener('pointerdown', closeFromOutside, true);
      window.removeEventListener('keydown', closeFromOutside, true);
    };
  }, [open]);

  const runAction = (action) => {
    setOpen(false);
    if (typeof action === 'function') action();
  };

  const itemStyle = {
    width: '100%',
    minHeight: 30,
    border: 0,
    borderRadius: 5,
    background: 'transparent',
    color: 'rgba(245,247,250,0.96)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
    padding: '0 9px',
    fontSize: 12,
    fontWeight: 850,
    cursor: 'pointer',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  };

  return (
    <div ref={menuRef} style={{ position: 'relative', flex: '0 0 auto' }}>
      <TopBarButton onClick={() => setOpen((value) => !value)} title="File actions">File ▾</TopBarButton>
      {open ? (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 32,
            zIndex: 100000,
            minWidth: 188,
            maxHeight: 'calc(100vh - 44px)',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            padding: 5,
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.14)',
            background: 'rgba(42,42,42,0.98)',
            boxShadow: '0 18px 46px rgba(0,0,0,0.48)',
          }}
        >
          {modelViewOpen ? (
            <>
              <button type="button" onClick={() => runAction(() => onExportModel && onExportModel('glb'))} style={itemStyle}>Export model to GLB</button>
              <button type="button" onClick={() => runAction(() => onExportModel && onExportModel('gltf'))} style={itemStyle}>Export model to glTF</button>
              <button type="button" onClick={() => runAction(() => onExportModel && onExportModel('obj'))} style={itemStyle}>Export model to OBJ</button>
              <button type="button" onClick={() => runAction(() => onExportModel && onExportModel('stl'))} style={itemStyle}>Export model to STL</button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => runAction(() => onSaveCanvas && onSaveCanvas('png'))} style={itemStyle}>Save to PNG</button>
              <button type="button" onClick={() => runAction(() => onSaveCanvas && onSaveCanvas('jpeg'))} style={itemStyle}>Save to JPEG</button>
            </>
          )}
          {/* In a drawing game only saving a copy of your own picture is offered: no projects, imports or 3D. */}
          {gameMode ? null : (
            <>
              <button type="button" onClick={() => runAction(onSaveCanvasProject)} style={itemStyle}>Save Voxemera file</button>
              <div style={{ height: 1, margin: '5px 3px', background: 'rgba(255,255,255,0.12)' }} />
              <button type="button" onClick={() => runAction(onLoadCanvasProject)} style={itemStyle}>Load Voxemera file</button>
            </>
          )}
          {!gameMode || gameMode.allowImport ? (
            <button type="button" onClick={() => runAction(onImportCanvasImage)} style={itemStyle}>Import image</button>
          ) : null}
          {/* Document paper: a 2D document property, so not offered for the 3D model. */}
          {!modelViewOpen && !gameMode ? (
            <>
              <div style={{ height: 1, margin: '5px 3px', background: 'rgba(255,255,255,0.12)' }} />
              <CanvasPaperSurfaceSubmenu itemStyle={itemStyle} onDone={() => setOpen(false)} />
            </>
          ) : null}
          {showDocumentSize ? (
            <>
              <div style={{ height: 1, margin: '5px 3px', background: 'rgba(255,255,255,0.12)' }} />
              <div style={{ padding: '4px 9px 7px' }}>
                <div style={{ marginBottom: 6, color: 'rgba(255,255,255,0.72)', fontSize: 11, fontWeight: 850 }}>Canvas size</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={draftDocumentWidth}
                    onChange={(event) => setDraftDocumentWidth(String(event.target.value || '').replace(/[^0-9]/g, ''))}
                    onKeyDown={(event) => { if (event.key === 'Enter') runAction(submitDocumentSize); }}
                    aria-label="Canvas width"
                    style={{ width: 66, height: 30, boxSizing: 'border-box', borderRadius: 5, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.92)', fontSize: 12, fontWeight: 800, padding: '0 7px', outline: 'none' }}
                  />
                  <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, fontWeight: 900 }}>×</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={draftDocumentHeight}
                    onChange={(event) => setDraftDocumentHeight(String(event.target.value || '').replace(/[^0-9]/g, ''))}
                    onKeyDown={(event) => { if (event.key === 'Enter') runAction(submitDocumentSize); }}
                    aria-label="Canvas height"
                    style={{ width: 66, height: 30, boxSizing: 'border-box', borderRadius: 5, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.92)', fontSize: 12, fontWeight: 800, padding: '0 7px', outline: 'none' }}
                  />
                  <button
                    type="button"
                    disabled={savingDocumentSize}
                    onClick={() => runAction(submitDocumentSize)}
                    style={{ ...itemStyle, width: 'auto', minWidth: 48, minHeight: 30, justifyContent: 'center', padding: '0 8px', background: 'rgba(255,255,255,0.07)' }}
                  >
                    {savingDocumentSize ? 'Set…' : 'Set'}
                  </button>
                </div>
              </div>
            </>
          ) : null}
          {!modelViewOpen && (canOpenCanvasAreas || canOpenCanvasGame || canEnableAnimation) ? (
            <>
              <div style={{ height: 1, margin: '5px 3px', background: 'rgba(255,255,255,0.12)' }} />
              {canEnableAnimation ? (
                animationEnabled ? (
                  <button type="button" onClick={() => runAction(onDisableAnimation)} style={{ ...itemStyle, color: 'rgba(255,224,224,0.98)' }}>Disable animation</button>
                ) : (
                  <button type="button" onClick={() => runAction(onEnableAnimation)} style={itemStyle}>Turn into animation</button>
                )
              ) : null}
              {canOpenCanvasAreas ? (
                <button type="button" onClick={() => runAction(onOpenCanvasAreas)} style={itemStyle}>Drawing areas</button>
              ) : null}
              {canOpenCanvasGame ? (
                <button type="button" onClick={() => runAction(onOpenCanvasGame)} style={itemStyle}>Classic draw &amp; guess (on this canvas)</button>
              ) : null}
            </>
          ) : null}
          {onOpenPartyGames ? (
            <button type="button" onClick={() => runAction(onOpenPartyGames)} style={itemStyle} data-testid="menu-party-games">Party Games…</button>
          ) : null}
          {gameMode ? null : (
            <>
              <div style={{ height: 1, margin: '5px 3px', background: 'rgba(255,255,255,0.12)' }} />
              <button type="button" onClick={() => runAction(onToggleModelView)} style={itemStyle}>
                {modelViewOpen ? '2D Mode' : '3D Model'}
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Divider() {
  return <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.14)', margin: '0 2px', flex: '0 0 1px' }} />;
}

function CompactReadout({ label, children, minWidth = 74 }) {
  return (
    <div
      title={label}
      style={{
        height: 28,
        minWidth,
        borderRadius: 5,
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(255,255,255,0.055)',
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '0 8px',
        boxSizing: 'border-box',
        color: 'rgba(245,247,250,0.94)',
        fontSize: 12,
        fontWeight: 850,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ color: 'rgba(255,255,255,0.58)', fontSize: 11, fontWeight: 850 }}>{label}</span>
      <span>{children}</span>
    </div>
  );
}

function SliderPill({ label, value, valueLabel, min, max, step = 1, onChange, disabled, width = 92, sizeCurve = false, previewKey = '', onPreviewStart, onPreviewEnd }) {
  const safeMin = Number.isFinite(Number(min)) ? Number(min) : 0;
  const safeMax = Number.isFinite(Number(max)) ? Number(max) : 100;
  const safeValue = clampNumber(value, safeMin, safeMax, safeMin);
  const useSizeCurve = !!sizeCurve && safeMax > safeMin;
  const sliderMin = useSizeCurve ? 0 : safeMin;
  const sliderMax = useSizeCurve ? SIZE_SLIDER_UI_MAX : safeMax;
  const sliderStep = useSizeCurve ? 1 : step;
  const sliderValue = useSizeCurve ? realToSizeSliderValue(safeValue, safeMin, safeMax) : safeValue;

  const startPreview = () => {
    if (!disabled && previewKey && typeof onPreviewStart === 'function') onPreviewStart(previewKey, label);
  };

  const endPreview = () => {
    if (previewKey && typeof onPreviewEnd === 'function') onPreviewEnd(previewKey, label);
  };

  return (
    <label
      title={label}
      style={{
        height: 28,
        borderRadius: 5,
        border: '1px solid rgba(255,255,255,0.12)',
        background: disabled ? 'rgba(255,255,255,0.030)' : 'rgba(255,255,255,0.055)',
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '0 8px',
        boxSizing: 'border-box',
        color: disabled ? 'rgba(255,255,255,0.35)' : 'rgba(245,247,250,0.94)',
        fontSize: 12,
        fontWeight: 850,
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.62 : 1,
      }}
    >
      <span style={{ color: 'rgba(255,255,255,0.58)', fontSize: 11, fontWeight: 850 }}>{label}</span>
      <input
        type="range"
        min={sliderMin}
        max={sliderMax}
        step={sliderStep}
        value={sliderValue}
        disabled={disabled}
        onPointerDown={startPreview}
        onPointerUp={endPreview}
        onPointerCancel={endPreview}
        onMouseDown={startPreview}
        onMouseUp={endPreview}
        onTouchStart={startPreview}
        onTouchEnd={endPreview}
        onTouchCancel={endPreview}
        onFocus={startPreview}
        onBlur={endPreview}
        onKeyDown={startPreview}
        onKeyUp={endPreview}
        onChange={(event) => {
          startPreview();
          if (typeof onChange === 'function') {
            const nextValue = useSizeCurve
              ? sizeSliderValueToReal(event.target.value, safeMin, safeMax)
              : Number(event.target.value);
            onChange(nextValue);
          }
        }}
        style={{ width, height: 18, margin: 0, accentColor: 'rgba(255,255,255,0.92)', cursor: disabled ? 'default' : 'pointer' }}
      />
      <span style={{ minWidth: 44, textAlign: 'right' }}>{valueLabel ?? Math.round(safeValue)}</span>
    </label>
  );
}

function TogglePill({ label, checked, onChange, disabled }) {
  return (
    <label
      title={label}
      style={{
        height: 28,
        borderRadius: 5,
        border: '1px solid rgba(255,255,255,0.12)',
        background: checked ? 'rgba(96,165,250,0.22)' : 'rgba(255,255,255,0.055)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '0 8px',
        boxSizing: 'border-box',
        color: disabled ? 'rgba(255,255,255,0.35)' : 'rgba(245,247,250,0.94)',
        fontSize: 11,
        fontWeight: 850,
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.62 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(event) => {
          if (typeof onChange === 'function') onChange(event.target.checked);
        }}
        style={{ margin: 0 }}
      />
      {label}
    </label>
  );
}



function DefaultBrushThumb() {
  return (
    <svg width="37" height="29" viewBox="0 0 28 22" aria-hidden="true" focusable="false" style={{ display: 'block' }}>
      <path
        d="M4.5 14.8C7.6 10.7 13.2 7.8 21.8 6.2C23.1 6 24 6.9 23.5 8C21.4 12.4 16.6 15.5 8.4 17.2C5.1 17.9 3.1 16.7 4.5 14.8Z"
        fill="#050505"
        opacity="0.94"
      />
      <path
        d="M6.2 14.9C9 12 13.4 9.8 20.2 8.3"
        fill="none"
        stroke="rgba(255,255,255,0.22)"
        strokeWidth="1.1"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ImportedBrushThumb({ brush }) {
  const dataUrl = brush && (brush.thumbnailDataUrl || brush.originalTipDataUrl);
  if (dataUrl) {
    return (
      <img
        src={dataUrl}
        alt=""
        draggable={false}
        style={{
          width: 37,
          height: 29,
          objectFit: 'contain',
          display: 'block',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
    );
  }

  return <DefaultBrushThumb />;
}

function BrushThumbnailBox({ brush, selected }) {
  return (
    <span
      style={{
        width: 45,
        height: 35,
        borderRadius: 5,
        border: selected ? '1px solid rgba(20,20,20,0.92)' : '1px solid rgba(0,0,0,0.18)',
        background: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible',
        flex: '0 0 45px',
        boxShadow: selected ? '0 0 0 1px rgba(255,255,255,0.35)' : 'inset 0 0 0 1px rgba(255,255,255,0.5)',
      }}
    >
      {brush ? <ImportedBrushThumb brush={brush} /> : <DefaultBrushThumb />}
    </span>
  );
}

function getBrushPickerPosition(button) {
  if (!button || typeof button.getBoundingClientRect !== 'function') return { top: 44, left: 8, width: 260 };
  const rect = button.getBoundingClientRect();
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth || 320 : 320;
  const width = Math.max(280, Math.min(420, rect.width || 300));
  const left = Math.max(8, Math.min(viewportWidth - width - 8, rect.left));
  return {
    top: Math.max(8, rect.bottom + 6),
    left,
    width,
  };
}


function PenBrushPickerControls({
  activeImportedBrush,
  importedBrushPresets,
  activeImportedBrushId,
  setActiveImportedBrushId,
  onAddImportedBrushPresets,
}) {
  const [importOpen, setImportOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 44, left: 8, width: 260 });
  const pickerButtonRef = useRef(null);
  const pickerMenuRef = useRef(null);
  const brushes = Array.isArray(importedBrushPresets) ? importedBrushPresets.filter(Boolean) : [];
  const activeName = activeImportedBrush ? (activeImportedBrush.name || 'Imported brush') : 'Default pen brush';

  function selectBrushId(id) {
    if (typeof setActiveImportedBrushId !== 'function') return;
    setActiveImportedBrushId(id ? String(id) : '');
    setPickerOpen(false);
  }

  function openPicker() {
    setPickerPosition(getBrushPickerPosition(pickerButtonRef.current));
    setPickerOpen((current) => !current);
  }

  useEffect(() => {
    if (!pickerOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (pickerButtonRef.current && pickerButtonRef.current.contains(target)) return;
      if (pickerMenuRef.current && pickerMenuRef.current.contains(target)) return;
      setPickerOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setPickerOpen(false);
    };

    const handleReposition = () => {
      setPickerPosition(getBrushPickerPosition(pickerButtonRef.current));
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [pickerOpen]);

  return (
    <>
      <div
        title={`Active brush: ${activeName}`}
        style={{
          height: 38,
          minWidth: 244,
          maxWidth: 328,
          borderRadius: 5,
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.055)',
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '0 8px',
          boxSizing: 'border-box',
          color: 'rgba(245,247,250,0.94)',
          fontSize: 12,
          fontWeight: 850,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          flex: '0 0 auto',
        }}
      >
        <span style={{ color: 'rgba(255,255,255,0.58)', fontSize: 11, fontWeight: 850 }}>Brush</span>
        <BrushThumbnailBox brush={activeImportedBrush} selected />
        <button
          ref={pickerButtonRef}
          type="button"
          onClick={openPicker}
          style={{
            minWidth: 0,
            flex: '1 1 auto',
            height: 32,
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 4,
            background: 'rgba(0,0,0,0.18)',
            color: 'rgba(245,247,250,0.96)',
            padding: '0 7px',
            boxSizing: 'border-box',
            fontSize: 12,
            fontWeight: 850,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            overflow: 'hidden',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{activeName}</span>
          <span aria-hidden="true" style={{ flex: '0 0 auto', opacity: 0.78 }}>▾</span>
        </button>
      </div>

      {pickerOpen ? (
        <div
          ref={pickerMenuRef}
          style={{
            position: 'fixed',
            top: pickerPosition.top,
            left: pickerPosition.left,
            width: pickerPosition.width,
            zIndex: 4000,
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.16)',
            background: 'rgba(35,35,35,0.98)',
            boxShadow: '0 14px 36px rgba(0,0,0,0.48)',
            padding: 6,
            boxSizing: 'border-box',
            maxHeight: 'min(480px, calc(100vh - 64px))',
            overflowY: 'auto',
          }}
        >
          <button
            type="button"
            onClick={() => selectBrushId('')}
            style={{
              width: '100%',
              minHeight: 50,
              border: 'none',
              borderRadius: 6,
              background: activeImportedBrushId ? 'transparent' : 'rgba(255,255,255,0.14)',
              color: 'rgba(245,247,250,0.96)',
              padding: '5px 7px',
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: 12,
              fontWeight: 850,
            }}
          >
            <BrushThumbnailBox brush={null} selected={!activeImportedBrushId} />
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Default pen brush</span>
          </button>

          {brushes.map((brush) => {
            const selected = !!brush && brush.id === activeImportedBrushId;
            return (
              <button
                key={brush.id}
                type="button"
                onClick={() => selectBrushId(brush.id)}
                style={{
                  width: '100%',
                  minHeight: 50,
                  border: 'none',
                  borderRadius: 6,
                  background: selected ? 'rgba(255,255,255,0.14)' : 'transparent',
                  color: 'rgba(245,247,250,0.96)',
                  padding: '5px 7px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 12,
                  fontWeight: 850,
                }}
              >
                <BrushThumbnailBox brush={brush} selected={selected} />
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{brush.name || 'Imported brush'}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      <TopBarButton onClick={() => setImportOpen(true)} title="Import / manage pen brushes">Import</TopBarButton>

      <ImportedBrushImportModal
        open={importOpen}
        importedBrushPresets={importedBrushPresets}
        activeImportedBrushId={activeImportedBrushId}
        onClose={() => setImportOpen(false)}
        onAddImportedBrushPresets={onAddImportedBrushPresets}
        onSetActiveImportedBrushId={setActiveImportedBrushId}
      />
    </>
  );
}

function normaliseCalligraphyPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.round(number * 100);
}

export default function CanvasTopMenuBar({
  activeTool,
  activeBrushPreset,
  importedBrushPresets,
  activeImportedBrushId,
  setActiveImportedBrushId,
  onAddImportedBrushPresets,
  activeToolOpacity,
  onToolOpacityChange,
  brushSize,
  setBrushSize,
  brushSoftness,
  setBrushSoftness,
  jitterAmount,
  setJitterAmount,
  calligraphySettings,
  setCalligraphySettings,
  airbrushDistance,
  setAirbrushDistance,
  airbrushPaintFlow,
  setAirbrushPaintFlow,
  airbrushPressurePaintFlowEnabled,
  setAirbrushPressurePaintFlowEnabled,
  airbrushAirPressure,
  setAirbrushAirPressure,
  liquifySettings,
  setLiquifySettings,
  displayZoomPercent,
  documentSize,
  isOwner,
  savingDocumentSize,
  onSetDocumentSize,
  historyCount,
  redoCount,
  onBack,
  onUndo,
  onRedo,
  onSaveCanvas,
  onSaveCanvasProject,
  onLoadCanvasProject,
  onImportCanvasImage,
  onExportModel,
  modelViewOpen,
  onToggleModelView,
  onSaveCanvasHistory,
  onClearCanvas,
  onZoomOut,
  onZoomReset,
  onZoomIn,
  onSliderPreviewChange,
  onOpenCanvasAreas,
  canOpenCanvasAreas,
  onOpenCanvasGame,
  canOpenCanvasGame,
  onOpenPartyGames,
  onEnableAnimation,
  onDisableAnimation,
  canEnableAnimation,
  animationEnabled,
  modelToolbarControls,
  gameMode = null,
}) {
  const opacityPercent = Math.round((Number.isFinite(activeToolOpacity) ? activeToolOpacity : 1) * 100);
  const toolSizeLimit = useViewportToolSizeLimit();
  const isMobileTopBar = useIsMobileTopBar();
  const safeBrushSize = Math.round(clampNumber(brushSize, 1, toolSizeLimit, 1));
  const zoomLabel = `${Math.round(Number(displayZoomPercent) || 0)}%`;
  const isPenTool = activeTool === 'pen';
  const isShapeTool = activeTool === 'line' || activeTool === 'rectangle' || activeTool === 'ellipse';
  const isBrushDrivenTool = isPenTool || isShapeTool;
  const isSelectionTool = activeTool === 'marquee_rect' || activeTool === 'marquee_ellipse' || activeTool === 'marquee_polygon' || activeTool === 'marquee_polygon_edit';
  const isViewTool = activeTool === 'grab' || activeTool === 'move';
  const isPaintTool = isPenTool || activeTool === 'eraser' || activeTool === 'smudge' || activeTool === 'liquify' || activeTool === 'line' || activeTool === 'rectangle' || activeTool === 'ellipse';
  const sizeDisabled = !isPaintTool || isSelectionTool || isViewTool;
  const opacityDisabled = isSelectionTool || isViewTool || activeTool === 'bucket';
  const isAirbrush = isBrushDrivenTool && activeBrushPreset === 'airbrush';
  const isJitter = isBrushDrivenTool && activeBrushPreset === 'jitter_blob';
  const isPencil = isBrushDrivenTool && activeBrushPreset === 'pencil';
  const isCalligraphy = isBrushDrivenTool && activeBrushPreset === 'calligraphy';
  const isLiquify = activeTool === 'liquify';
  const safeLiquifySettings = normaliseLiquifySettings(liquifySettings);
  const opacityLabel = activeTool === 'smudge' || activeTool === 'liquify' ? 'Intensity' : 'Opacity';
  const safeCalligraphySettings = calligraphySettings || {};
  const safeImportedBrushPresets = Array.isArray(importedBrushPresets) ? importedBrushPresets.filter(Boolean) : [];
  const activeImportedBrush = safeImportedBrushPresets.find((brush) => brush && brush.id === activeImportedBrushId) || null;

  const [draftDocumentWidth, setDraftDocumentWidth] = useState(() => String(clampDocumentDimension(documentSize && documentSize.width, 1600)));
  const [draftDocumentHeight, setDraftDocumentHeight] = useState(() => String(clampDocumentDimension(documentSize && documentSize.height, 1000)));
  const activeShapePreviewKeyRef = useRef('');

  useEffect(() => {
    setDraftDocumentWidth(String(clampDocumentDimension(documentSize && documentSize.width, 1600)));
    setDraftDocumentHeight(String(clampDocumentDimension(documentSize && documentSize.height, 1000)));
  }, [documentSize]);

  const submitDocumentSize = useCallback(() => {
    if (!isOwner || savingDocumentSize || typeof onSetDocumentSize !== 'function') return;
    const widthText = String(draftDocumentWidth || '').trim();
    const heightText = String(draftDocumentHeight || '').trim();
    if (!widthText || !heightText) return;
    const nextWidth = clampDocumentDimension(widthText, documentSize && documentSize.width ? documentSize.width : 1600);
    const nextHeight = clampDocumentDimension(heightText, documentSize && documentSize.height ? documentSize.height : 1000);
    setDraftDocumentWidth(String(nextWidth));
    setDraftDocumentHeight(String(nextHeight));
    onSetDocumentSize(nextWidth, nextHeight);
  }, [documentSize, draftDocumentHeight, draftDocumentWidth, isOwner, onSetDocumentSize, savingDocumentSize]);

  const handleSliderPreviewStart = useCallback((previewKey, label) => {
    const safePreviewKey = String(previewKey || '').trim();
    if (!safePreviewKey) return;

    activeShapePreviewKeyRef.current = safePreviewKey;

    if (typeof onSliderPreviewChange === 'function') {
      onSliderPreviewChange({ active: true, key: safePreviewKey, label: label || '' });
    }
  }, [onSliderPreviewChange]);

  const handleSliderPreviewEnd = useCallback((previewKey) => {
    const safePreviewKey = String(previewKey || '').trim();
    if (safePreviewKey && activeShapePreviewKeyRef.current && activeShapePreviewKeyRef.current !== safePreviewKey) return;

    activeShapePreviewKeyRef.current = '';

    if (typeof onSliderPreviewChange === 'function') {
      onSliderPreviewChange({ active: false, key: '', label: '' });
    }
  }, [onSliderPreviewChange]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    function stopShapeSliderPreview() {
      if (!activeShapePreviewKeyRef.current) return;
      activeShapePreviewKeyRef.current = '';
      if (typeof onSliderPreviewChange === 'function') {
        onSliderPreviewChange({ active: false, key: '', label: '' });
      }
    }

    function handleWindowKeyDown(event) {
      if (event.key === 'Escape') stopShapeSliderPreview();
    }

    window.addEventListener('pointerup', stopShapeSliderPreview, true);
    window.addEventListener('pointercancel', stopShapeSliderPreview, true);
    window.addEventListener('mouseup', stopShapeSliderPreview, true);
    window.addEventListener('touchend', stopShapeSliderPreview, true);
    window.addEventListener('touchcancel', stopShapeSliderPreview, true);
    window.addEventListener('blur', stopShapeSliderPreview);
    window.addEventListener('keydown', handleWindowKeyDown, true);

    return () => {
      window.removeEventListener('pointerup', stopShapeSliderPreview, true);
      window.removeEventListener('pointercancel', stopShapeSliderPreview, true);
      window.removeEventListener('mouseup', stopShapeSliderPreview, true);
      window.removeEventListener('touchend', stopShapeSliderPreview, true);
      window.removeEventListener('touchcancel', stopShapeSliderPreview, true);
      window.removeEventListener('blur', stopShapeSliderPreview);
      window.removeEventListener('keydown', handleWindowKeyDown, true);
      stopShapeSliderPreview();
    };
  }, [onSliderPreviewChange]);

  const shapeSliderPreviewProps = {
    onPreviewStart: handleSliderPreviewStart,
    onPreviewEnd: handleSliderPreviewEnd,
  };

  useEffect(() => {
    const number = Number(brushSize);
    if (!Number.isFinite(number) || number <= toolSizeLimit || typeof setBrushSize !== 'function') return;
    setBrushSize(toolSizeLimit);
  }, [brushSize, setBrushSize, toolSizeLimit]);

  function updateCalligraphySetting(key, value) {
    if (typeof setCalligraphySettings !== 'function') return;
    setCalligraphySettings((current) => ({
      ...(current || {}),
      [key]: value,
    }));
  }


  function updateLiquifySetting(key, value) {
    if (typeof setLiquifySettings !== 'function') return;
    setLiquifySettings((current) => ({
      ...(current || {}),
      [key]: normaliseLiquifySettings({ [key]: value })[key],
    }));
  }

  return (
    <div
      style={{
        flex: '0 0 auto',
        minHeight: 42,
        borderBottom: '1px solid rgba(255,255,255,0.10)',
        background: 'linear-gradient(to bottom, #464646, #393939)',
        boxShadow: '0 1px 0 rgba(0,0,0,0.55)',
        color: 'rgba(242,244,247,0.94)',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: isMobileTopBar ? 4 : 7,
        padding: isMobileTopBar ? '6px 5px' : '6px 8px',
        boxSizing: 'border-box',
        overflow: 'visible',
        maxWidth: '100%',
      }}
    >
      <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: isMobileTopBar ? 4 : 7 }}>
        {typeof onBack === 'function' ? <TopBarButton onClick={onBack} title="Back to canvases">←</TopBarButton> : null}
        <FileDropdownButton
          gameMode={gameMode}
          onSaveCanvas={onSaveCanvas}
          onSaveCanvasProject={onSaveCanvasProject || onSaveCanvasHistory}
          onLoadCanvasProject={onLoadCanvasProject}
          onImportCanvasImage={onImportCanvasImage}
          onExportModel={onExportModel}
          modelViewOpen={modelViewOpen}
          onToggleModelView={onToggleModelView}
          onOpenCanvasAreas={onOpenCanvasAreas}
          canOpenCanvasAreas={canOpenCanvasAreas}
          onOpenCanvasGame={onOpenCanvasGame}
          canOpenCanvasGame={canOpenCanvasGame}
          onOpenPartyGames={onOpenPartyGames}
          onEnableAnimation={onEnableAnimation}
          onDisableAnimation={onDisableAnimation}
          canEnableAnimation={canEnableAnimation}
          animationEnabled={animationEnabled}
          showDocumentSize={isMobileTopBar && isOwner && !modelViewOpen}
          draftDocumentWidth={draftDocumentWidth}
          draftDocumentHeight={draftDocumentHeight}
          setDraftDocumentWidth={setDraftDocumentWidth}
          setDraftDocumentHeight={setDraftDocumentHeight}
          submitDocumentSize={submitDocumentSize}
          savingDocumentSize={savingDocumentSize}
        />
        <TopBarButton onClick={onUndo} disabled={!historyCount || Boolean(gameMode && gameMode.noUndo)} title={gameMode && gameMode.noUndo ? 'Undo is off in this round' : 'Undo'}>↶</TopBarButton>
        <TopBarButton onClick={onRedo} disabled={!redoCount || Boolean(gameMode && gameMode.noUndo)} title="Redo">↷</TopBarButton>
        {gameMode && gameMode.noClear ? null : <TopBarButton onClick={onClearCanvas} title="Clear canvas" danger>Clear</TopBarButton>}
      </div>

      <Divider />

      {modelViewOpen ? (
        <>
          <div
            id="canvas-3d-toolbar-controls"
            style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '0 0 auto', position: 'relative' }}
          >
            {modelToolbarControls || null}
          </div>
          <Divider />
        </>
      ) : isOwner && !isMobileTopBar ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '0 0 auto' }} title="Canvas document size">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={draftDocumentWidth}
              onChange={(event) => setDraftDocumentWidth(String(event.target.value || '').replace(/[^0-9]/g, ''))}
              onKeyDown={(event) => { if (event.key === 'Enter') submitDocumentSize(); }}
              aria-label="Canvas width"
              style={{ width: 58, height: 28, boxSizing: 'border-box', borderRadius: 5, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.92)', fontSize: 12, fontWeight: 800, padding: '0 6px', outline: 'none' }}
            />
            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, fontWeight: 900 }}>×</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={draftDocumentHeight}
              onChange={(event) => setDraftDocumentHeight(String(event.target.value || '').replace(/[^0-9]/g, ''))}
              onKeyDown={(event) => { if (event.key === 'Enter') submitDocumentSize(); }}
              aria-label="Canvas height"
              style={{ width: 58, height: 28, boxSizing: 'border-box', borderRadius: 5, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.92)', fontSize: 12, fontWeight: 800, padding: '0 6px', outline: 'none' }}
            />
            <TopBarButton onClick={submitDocumentSize} disabled={savingDocumentSize} title="Set canvas size">
              {savingDocumentSize ? 'Set…' : 'Set'}
            </TopBarButton>
          </div>
          <Divider />
        </>
      ) : null}

      <div style={{ flex: '1 1 auto', minWidth: 0 }} />

      {!isMobileTopBar ? <Divider /> : null}

      <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: isMobileTopBar ? 4 : 7 }}>
        <TopBarButton onClick={onZoomOut} title="Zoom out">−</TopBarButton>
        <TopBarButton onClick={onZoomReset} title="Fit view">Fit</TopBarButton>
        <TopBarButton onClick={onZoomIn} title="Zoom in">+</TopBarButton>
        {!isMobileTopBar ? <CompactReadout label="Zoom">{zoomLabel}</CompactReadout> : null}
      </div>
    </div>
  );
}
